import { SearchFilterContentTypes } from 'stockblocks-client';

import { ButtonProps } from '@videoblocks/design-system';

import { defaultContentTypes } from '../blocks/ProminentSearch.builder';

const BUTTON_VARIANTS = ['primary', 'secondary', 'outlined-dark', 'outlined-light', 'text', 'alternate'];

const REL_VALUES = [
    'alternate',
    'author',
    'bookmark',
    'external',
    'help',
    'license',
    'next',
    'prev',
    'search',
    'tag',
    'nofollow',
    'noopener',
    'noreferrer',
    'dns-prefetch',
    'preconnect',
    'preload',
    'prefetch',
    'prerender',
    'archives',
    'stylesheet',
    'pingback',
];

export type ProminentSearchButtons = ButtonProps & {
    text: string;
    href?: string;
    showCallToAction: boolean;
    mobileOnly?: boolean;
};

export const config = {
    name: 'Prominent Search',
    inputs: [
        {
            name: 'headingFirstLine',
            type: 'string',
            required: false,
            defaultValue: 'Tell all your best stories of the',
        },
        {
            name: 'headingSecondLine',
            type: 'string',
            required: false,
            defaultValue: 'world. Lorem ipsum dolor sit',
        },
        {
            name: 'trendingTags',
            type: 'string',
            required: false,
            defaultValue: '',
            helperText: 'Comma-separated list of trending tags',
        },
        {
            name: 'videoSrc',
            type: 'file',
            allowedFileTypes: ['webm', 'mp4'],
            required: false,
            defaultValue: '',
            helperText: 'Path of background video file',
        },
        {
            name: 'posterSrc',
            type: 'file',
            allowedFileTypes: ['jpg', 'png'],
            required: false,
            defaultValue: '/home/video-homepage-hero-background.jpg',
            helperText: 'Path of background poster file',
        },
        {
            name: 'posterAlt',
            type: 'string',
            required: false,
            defaultValue: 'Background image',
        },
        {
            name: 'contentTypes',
            type: 'list',
            required: false,
            helperText: 'List of clickable content types / options',
            defaultValue: defaultContentTypes,
            subFields: [
                {
                    name: 'id',
                    type: 'string',
                    required: true,
                    enum: Object.values(SearchFilterContentTypes),
                    defaultValue: SearchFilterContentTypes.All_videos_content_type,
                    helperText: 'URL root',
                },
                {
                    name: 'label',
                    type: 'string',
                    required: true,
                    defaultValue: '[Label]',
                    helperText: 'Text label for content type',
                },
                {
                    name: 'placeholderText',
                    type: 'string',
                    required: true,
                    defaultValue: 'Search [CONTENT] library...',
                    helperText: 'Search box placeholder text',
                },
            ],
        },
        {
            name: 'socialProof',
            type: 'list',
            helperText: 'Add logos',
            required: false,
            subFields: [
                {
                    name: 'logoAlt',
                    type: 'string',
                },
                {
                    name: 'logoImage',
                    type: 'file',
                    allowedFileTypes: ['jpg', 'svg'],
                    helperText: 'upload image file (jpg or svg)',
                },
            ],
            onChange: (options) => {
                if (options.get('socialProof').length > 8) {
                    options.set('socialProof', options.get('socialProof').slice(0, 8));
                    alert('Maximum number of logos is 8. Modify an existing logo instead.');
                }
            },
        },
        {
            name: 'showCallToAction',
            type: 'boolean',
            defaultValue: false,
        },
        {
            name: 'callToAction',
            type: 'object',
            defaultValue: {
                text: 'Explore',
                href: '/',
                variant: BUTTON_VARIANTS[0],
                size: 'default',
                target: '_self',
                rel: '',
            },
            showIf: (options) => options.get('showCallToAction'),
            subFields: [
                {
                    name: 'text',
                    type: 'string',
                    default: '',
                    required: true,
                },
                {
                    name: 'href',
                    type: 'string',
                    default: '/',
                    required: true,
                },
                {
                    name: 'target',
                    type: 'string',
                    enum: ['_self', '_blank', '_parent', '_top'],
                    helperText:
                        'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
                },
                {
                    name: 'rel',
                    type: 'string',
                    enum: REL_VALUES,
                    helperText:
                        'For documentation on accepted values, see https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types for link types allowed in <a> elements',
                },
                {
                    name: 'variant',
                    type: 'string',
                    default: BUTTON_VARIANTS[0],
                    enum: BUTTON_VARIANTS,
                },
                {
                    name: 'size',
                    type: 'string',
                    default: 'default',
                    enum: ['default', 'large'],
                },
                {
                    name: 'disabled',
                    type: 'boolean',
                    defaultValue: false,
                    enum: [
                        {
                            label: 'Enabled',
                            value: false,
                        },
                        {
                            label: 'Disabled',
                            value: true,
                        },
                    ],
                },
            ],
        },
        {
            name: 'showSecondaryCta',
            type: 'boolean',
            defaultValue: false,
        },
        {
            name: 'secondaryCta',
            type: 'object',
            defaultValue: {
                text: 'Secondary CTA',
                href: '/',
                variant: BUTTON_VARIANTS[3],
                size: 'default',
                target: '_self',
                rel: '',
            },
            showIf: (options) => options.get('showSecondaryCta'),
            subFields: [
                {
                    name: 'text',
                    type: 'string',
                    default: '',
                    required: true,
                },
                {
                    name: 'href',
                    type: 'string',
                    default: '/',
                    required: true,
                },
                {
                    name: 'variant',
                    type: 'string',
                    default: BUTTON_VARIANTS[1],
                    enum: BUTTON_VARIANTS,
                },
                {
                    name: 'size',
                    type: 'string',
                    default: 'default',
                    enum: ['default', 'large'],
                },
                {
                    name: 'disabled',
                    type: 'boolean',
                    defaultValue: false,
                    enum: [
                        {
                            label: 'Enabled',
                            value: false,
                        },
                        {
                            label: 'Disabled',
                            value: true,
                        },
                    ],
                },
                {
                    name: 'target',
                    type: 'string',
                    enum: ['_self', '_blank', '_parent', '_top'],
                    helperText:
                        'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
                },
                {
                    name: 'rel',
                    type: 'string',
                    enum: REL_VALUES,
                    helperText:
                        'For documentation on accepted values, see https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types for link types allowed in <a> elements',
                },
            ],
        },
    ],
};
